<template>
  <TrackingProvider>
    <Head>
      <meta
        v-if="!allowRobots"
        name="robots"
        content="none"
      >
    </Head>

    <div class="flex flex-col items-stretch min-h-screen bg-vp-base-100">
      <hgroup
        class="inset-x-0 top-0 text-vp-base-900 checkout border-b border-vp-base-200 font-bold flex justify-center h-16"
      >
        <header class="container mx-auto p-3 flex justify-between items-center min-w-7xl">
          <div class="flex items-center gap-4">
            <div
              v-if="headerLogo"
              class="h-12 overflow-hidden"
            >
              <img
                :alt="`${headerTitle} logo`"
                :src="headerLogo"
                class="object-contain h-full max-h-full max-w-full"
              >
            </div>

            <VPCountdown
              v-if="targetTime"
              :target-time="targetTime"
              @expired="onCountdownExpired"
            />
          </div>
          <div class="flex flex-col items-end">
            <a
              href="https://my.venuepilot.co"
              target="_blank"
              class="text-xs sm:text-sm font-normal"
            >
              {{ $gettext('Find my tickets') }}
              <iconify-icon
                icon="mdi:open-in-new"
                class="text-sm -mb-0.5"
              />
            </a>
            <button
              type="button"
              class="md:hidden text-sm"
              @click="activateHubspotChat"
            >{{ $gettext('Need help?') }}</button>
          </div>
        </header>
      </hgroup>
      <main class="container mx-auto flex justify-center lg:grow">
        <RecaptchaProvider>
          <slot />
        </RecaptchaProvider>
      </main>
    </div>
    <Teleport
      v-if="!rootStore.isDefaultApiBase"
      to="#vp-teleport-alert"
    >
      <div class="bg-red-600 text-white py-2 text-center">
        <div class="font-bold text-lg">
          Using Non-default server: <code>{{ rootStore.apiBase }}</code>.
        </div>
        <button
          type="button"
          @click="resetServer"
        >
          Click here to reset to <code>{{ rootStore.defaultApiBase }}</code>
        </button>
      </div>
    </Teleport>
  </TrackingProvider>
</template>

<script setup lang="ts">
import { Head } from 'vike-vue/Head';
import 'vue-toastification/dist/index.css';
import '@/checkout/checkout.css';
import { useGettext } from 'vue3-gettext';
import RecaptchaProvider from '@/components/recaptcha/RecaptchaProvider.vue';
import VPCountdown from '@/components/VPCountdown.vue';
import { useCheckoutStore } from '@/stores/checkout';
import { computed, watch } from 'vue';
import { useRootStore } from '@/stores/root';
import { useConfig } from 'vike-vue/useConfig';
import { useCheckoutTitle } from '@/checkout/useCheckoutTitle';
import TrackingProvider from '@/components/tracking/TrackingProvider.vue';

const { $gettext } = useGettext();
const rootStore = useRootStore();
const config = useConfig();
const { title } = useCheckoutTitle();

watch(
  () => title.value,
  newValue => {
    config({ title: newValue });
  },
);

const checkoutStore = useCheckoutStore();

const onCountdownExpired = async () => {
  checkoutStore.releaseLock();
  checkoutStore.reset();
  alert($gettext('Time is up!'));
  window.location.reload();
};

const targetTime = computed(() => checkoutStore.shoppingCart?.lockExpiresAt);

const resetServer = () => {
  rootStore.setApiBase(undefined);
  if (!import.meta.env.SSR) {
    window.location.reload();
  }
};

const allowRobots = import.meta.env.VITE_ALLOW_ROBOTS == '1';

const headerTitle = computed(() => checkoutStore.getSelectedEvent?.venue?.name || 'VenuePilot');
const headerLogo = computed(() => checkoutStore.getSelectedEvent?.venue?.logoUrl);

const activateHubspotChat = () => {
  if (window.HubSpotConversations) {
    document.body.setAttribute('data-unhide-hubspot-messages', '1');
    window.HubSpotConversations.widget.open();

    window.HubSpotConversations.on('widgetClosed', () => document.body.setAttribute('data-unhide-hubspot-messages', '0'));
  }
};
</script>
